import styled, { keyframes } from "styled-components";

export const BackButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  padding: 20px;
`;

export const BackButton = styled.button`
  background-color: transparent;
  color: #ff6d1d;
  font-size: 16px;
  font-weight: bold;
  border: none;
  cursor: pointer;
  padding: 10px;
  font-family: "SF Pro Text";
  position: relative;
  display: inline-block;
  transition: opacity 180ms ease-in-out;

  &:hover {
    opacity: 0.8;
  }

  /* Cross-fade label layers */
  .fade-layer {
    position: absolute;
    left: 10px;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    transition: opacity 200ms ease-in-out;
    white-space: nowrap;
    text-align: left;
  }

  .fade-layer.hidden {
    opacity: 0;
    pointer-events: none;
  }

  .fade-layer.visible {
    opacity: 1;
  }
`;

// Subtle shimmer skeleton for inline saving indicator
const savingShimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

export const SavingSkeleton = styled.span`
  display: inline-block;
  color: transparent;
  background-image: linear-gradient(90deg, #8a8a8a 0%, #8a8a8a 100%);
  background-size: 300% 100%;
  background-repeat: no-repeat;
  background-position: 0 0;
  -webkit-background-clip: text;
  background-clip: text;
  animation: ${savingShimmer} 1s ease-in-out infinite; /* slower shimmer */
  font-size: 14px;
  font-weight: 500;
`;

export const SavingStatusContainer = styled.span`
  position: relative;
  display: inline-block;
  min-width: 90px;
  height: 18px;
`;

export const StatusLayer = styled.span`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  transition: opacity 300ms ease-in-out; /* smooth fade */
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  white-space: nowrap;
  color: #666;
  font-size: 14px;
  font-weight: 500;
`;

export const ValueInput = styled.input`
  text-align: right;
  border: 1px solid #d4d4d4;
  border-radius: 0;
  background-color: #ffffff;
  color: #333;
  outline: none;
  width: 100%;
  min-width: 130px;  
  max-width: 200px;  
  padding: 8px 12px;
  font-family: "Segoe UI", Arial, sans-serif;
  font-size: 16px;
  line-height: 1.2;
  transition: all 0.1s ease;
  box-sizing: border-box;

  &:hover {
    border-color: #ff6d1d;
    background-color: #f8f8f8;
  }

  &:focus {
    border: 2px solid #ff6d1d;
    background-color: #ffffff;
    box-shadow: 0 0 0 1px #ff6d1d;
    z-index: 1;
    position: relative;
  }

  &:not(:focus):not(:hover) {
    border-right: 1px solid #d4d4d4;
    border-bottom: 1px solid #d4d4d4;
  }
`;

export const TopLevelInput = styled.input`
  text-align: left;
  border: 1px solid #d4d4d4;
  border-radius: 0;
  background-color: #ffffff;
  color: #333;
  outline: none;
  width: 100%;
  min-width: 150px; /* Smaller min-width for text fields */
  max-width: 300px; /* Allow more width for company names */
  padding: 8px 12px;
  font-family: "Segoe UI", Arial, sans-serif;
  font-size: 16px;
  line-height: 1.2;
  transition: all 0.1s ease;
  box-sizing: border-box;

  &:hover {
    border-color: #ff6d1d;
    background-color: #f8f8f8;
  }

  &:focus {
    border: 2px solid #ff6d1d;
    background-color: #ffffff;
    box-shadow: 0 0 0 1px #ff6d1d;
    z-index: 1;
    position: relative;
  }

  &:not(:focus):not(:hover) {
    border-right: 1px solid #d4d4d4;
    border-bottom: 1px solid #d4d4d4;
  }
`;

export const ActionButton = styled.button`
  cursor: pointer;
  border: 1px solid #d4d4d4;
  background: #f8f9fa;
  color: #333;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: bold;
  border-radius: 2px;
  transition: all 0.1s ease;
  min-width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #e1e7ec;
    border-color: #70ad47;
  }

  &:focus {
    outline: 2px solid #0078d4;
    outline-offset: 1px;
  }

  &:active {
    background-color: #d3d9df;
  }
`;

// Excel-like table container
export const ExcelTableContainer = styled.div`
  background-color: #ffffff;
  border: 1px solid #d4d4d4;
  border-radius: 0;
  overflow: auto;
  font-family: "Segoe UI", Arial, sans-serif;
  
  /* Scrollbar styling to match Excel */
  &::-webkit-scrollbar {
    width: 16px;
    height: 16px;
  }

  &::-webkit-scrollbar-track {
    background-color: #f1f1f1;
    border: 1px solid #d4d4d4;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #c1c1c1;
    border: 1px solid #d4d4d4;
    border-radius: 0;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #a8a8a8;
  }

  &::-webkit-scrollbar-corner {
    background-color: #f1f1f1;
  }
`;

// Excel-like table styling
export const ExcelTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  font-family: "Segoe UI", Arial, sans-serif;
  font-size: 11px;
  background-color: #ffffff;
`;

// Excel-like header row
export const ExcelHeaderRow = styled.tr`
  background-color: #f2f2f2;
  border-bottom: 1px solid #d4d4d4;
  height: 20px;
  
  &:hover {
    background-color: #e8e8e8;
  }
`;

// Excel-like header cell
export const ExcelHeaderCell = styled.th`
  background-color: #f2f2f2;
  border: 1px solid #d4d4d4;
  border-top: none;
  border-left: none;
  padding: 4px 8px;
  text-align: center;
  font-weight: normal;
  font-size: 11px;
  color: #333;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 10;
  min-width: 80px;
  
  &:first-child {
    border-left: 1px solid #d4d4d4;
  }
  
  &:hover {
    background-color: #e8e8e8;
  }
`;

// Excel-like data row
export const ExcelRow = styled.tr`
  background-color: #ffffff;
  border: none;
  height: 20px;
  
  &:hover {
    background-color: #f8f8f8;
  }
  
  &:nth-child(even) {
    background-color: #ffffff;
  }
  
  &:nth-child(odd) {
    background-color: #ffffff;
  }
  
  &:hover {
    background-color: #cce7ff;
  }
`;

// Excel-like data cell
export const ExcelCell = styled.td`
  border: 1px solid #d4d4d4;
  border-top: none;
  border-left: none;
  padding: 0;
  text-align: left;
  font-size: 11px;
  color: #333;
  background-color: inherit;
  position: relative;
  min-width: 80px;
  height: 20px;
  
  &:first-child {
    border-left: 1px solid #d4d4d4;
    background-color: #f2f2f2;
    font-weight: normal;
    text-align: center;
    padding: 4px 8px;
    min-width: 200px;
  }
  
  &:focus-within {
    border: 2px solid #0078d4;
    z-index: 2;
  }
`;

// Excel-like totals row
export const ExcelTotalsRow = styled(ExcelRow)`
  background-color: #f2f2f2;
  border-top: 2px solid #333;
  font-weight: bold;
  
  &:hover {
    background-color: #e8e8e8;
  }
`;

// Excel-like totals cell
export const ExcelTotalsCell = styled(ExcelCell)`
  background-color: #f2f2f2;
  font-weight: bold;
  
  &:first-child {
    background-color: #e8e8e8;
  }
`;
